/** The shapes the adventure shell passes between its own panels. */

import type { HeroId, ObjectId, Reason, TownId } from "@game/core";

/** What the pointer is over. The canvas reports cells and world entities; a
 *  panel reports the entity its own row stands for. */
export type HoverTarget =
  | { readonly kind: "NONE" }
  | { readonly kind: "CELL"; readonly x: number; readonly y: number; readonly z: number }
  | { readonly kind: "HERO"; readonly id: HeroId }
  | { readonly kind: "TOWN"; readonly id: TownId }
  | { readonly kind: "OBJECT"; readonly id: ObjectId };

export const NO_HOVER: HoverTarget = Object.freeze({ kind: "NONE" });

/** What the player has picked out in the sidebar. Both may be set: the classic keeps
 *  a hero selected while you look at a town. */
export interface Selection {
  readonly heroId: HeroId | null;
  readonly townId: TownId | null;
}

export const NO_SELECTION: Selection = Object.freeze({ heroId: null, townId: null });

/**
 * The cost of the path the pointer is currently proposing. Supplied by the
 * host, which got it from the kernel's `pathCost` - the shell does not compute
 * a route (I10).
 */
export interface MoveEstimate {
  readonly costPoints: number;
  /** Whole turns the move will take at the hero's current daily pool. */
  readonly turns: number;
  readonly reachable: boolean;
}

/**
 * Everything the status bar needs. `reason` is the structured refusal straight
 * from the kernel; the bar renders it with core's own `reasonText` so that the
 * wording of an illegality lives in exactly one place.
 */
export interface StatusState {
  readonly hover: HoverTarget;
  readonly reason: Reason | null;
  readonly move: MoveEstimate | null;
}

/**
 * Movement and mana ceilings for a hero. Both are rule outputs (`dailyMovement`
 * and `maxMana` in the adventure kernel, with skills, artifacts and terrain
 * folded in), so the host supplies them; the shell only draws the gauge. When
 * a hero is missing from the map the bars fall back to a display-only ceiling
 * and say so in their tooltip.
 */
export interface HeroCapacity {
  readonly movementMax: number;
  readonly manaMax: number;
}

export type HeroCapacities = Readonly<Record<string, HeroCapacity>>;
